import { X, ImageUp, SquarePen, Trash2 } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import transparentBg from "../assets/images/transparent-bg.png";
import { PatientService } from "../services/api/patient.service";
import { Patient } from "../types";
import { AuthService } from "../services/api";
import { TextField, ThemeProvider } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import customTheme from "../components/ui/CustomTheme";

interface AddNewPatientModalProps {
  mobileNumber: string;
  onClose: () => void;
  onSuccess: (patientData: NewPatientData) => void;
  initialData: Patient | null;
  isVerifiedProps: boolean | null;
}

interface NewPatientData {
  id: string;
  userId: string;
  name: string;
  firstName: string; // New field
  lastName: string; // New field
  age: string;
  gender: "Male" | "Female" | "Other";
  profileImage?: File | string;
  role: string;
  aadharNumber: string;
  dateOfBirth: string;
  address: string;
  pincode: string;
  city: string;
  state: string;
}
// For showing age with string for all screens
export const formatAge = (ageString: string): string => {
  if (!ageString) return "N/A"; // Fallback for invalid input

  // Extract years, months, and days from the string
  const yearsMatch = ageString.match(/(\d+)\s*yrs?/);
  const monthsMatch = ageString.match(/(\d+)\s*months?/);
  const daysMatch = ageString.match(/(\d+)\s*days?/);

  const years = yearsMatch ? parseInt(yearsMatch[1], 10) : 0;
  const months = monthsMatch ? parseInt(monthsMatch[1], 10) : 0;
  const days = daysMatch ? parseInt(daysMatch[1], 10) : 0;

  // Format based on conditions
  if (years > 0) {
    return `${years} yrs ${months} months`; // Show years and months
  } else if (months > 0) {
    return `${months} months`; // Show only months,If patient age is less than one year
  } else {
    return `${days} days`; // Show only days,If patient age is less than one month
  }
};

export function AddNewPatientModal({
  mobileNumber: initialMobileNumber,
  onClose,
  onSuccess,
  initialData,
  isVerifiedProps,
}: AddNewPatientModalProps) {
  const [isDragging, setIsDragging] = useState(false);
  const dropZoneRef = useRef<HTMLLabelElement>(null);
  const [mobileNumber, setMobileNumber] = useState(initialMobileNumber);
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [timeLeft, setTimeLeft] = useState("00:00");
  const [isVerifying, setIsVerifying] = useState(false);
  // const [isVerified, setIsVerified] = useState(isVerifiedProps);
  const [isVerified, setIsVerified] = useState(true);
  const [timerOn, setTimerOn] = useState(false);
  const [patientData, setPatientData] = useState<NewPatientData>({
    id: "",
    userId: "",
    firstName: "", // New field
    lastName: "", // New field
    name: "",
    age: "",
    gender: "Male",
    role: "Patient",
    profileImage: "",
    aadharNumber: "",
    dateOfBirth: "",
    address: "",
    pincode: "",
    city: "",
    state: "",
  });

  useEffect(() => {
    if (initialData) {
      // Split the name field into firstName and lastName
      const nameParts = initialData.name.split(" ");
      const firstName = nameParts[0] || "";
      const lastName = nameParts.slice(1).join(" ") || "";

      setPatientData({
        ...initialData,
        firstName,
        lastName,
      });
    }
  }, [initialData]);

  const calculateAge = (dob: string) => {
    if (!dob) return { years: 0, months: 0, days: 0 }; // Prevents undefined error

    const birthDate = new Date(dob);
    const today = new Date();

    let years = today.getFullYear() - birthDate.getFullYear();
    let months = today.getMonth() - birthDate.getMonth();
    let days = today.getDate() - birthDate.getDate();

    if (days < 0) {
      const lastMonthDays = new Date(
        today.getFullYear(),
        today.getMonth(),
        0,
      ).getDate();
      days += lastMonthDays;
      months--;
    }

    if (months < 0) {
      years--;
      months += 12;
    }

    if (years > 0) {
      return `${years} yrs ${months} months`;
    } else if (months > 0) {
      return `${months} months`;
    } else {
      return `${days} days`;
    }
  };

  // For showing age in string dob field
  const formatAgeString = (
    age: { years: number; months: number; days: number } | string | null,
  ) => {
    if (!age) return "N/A";

    // If age is already a string, return it directly
    if (typeof age === "string") return age;

    const { years, months, days } = age;
    return years > 0
      ? `${years} yrs ${months} months`
      : months > 0
        ? `${months} months`
        : `${days} days`;
  };

  // Handle Date of Birth change
  // Converts Date object to 'YYYY-MM-DD' format and calculates age
  const handleDOBChange = (dob: Date | null) => {
    if (dob instanceof Date && !isNaN(dob.getTime())) {
      const formattedDob = dob.toISOString().split("T")[0]; // 'YYYY-MM-DD'
      const ageData = calculateAge(formattedDob);

      console.log("Age Data Stored:", ageData);

      setPatientData((prev) => ({
        ...prev,
        dateOfBirth: formattedDob,
        age: ageData,
      }));
    } else {
      console.warn("Invalid date passed to handleDOBChange:", dob);
    }
  };

  // Formats DOB for input field
  // Converts 'YYYY-MM-DD' to Date object for DatePicker
  const formatDateOfBirth = (dob: string): Date | null => {
    return dob ? new Date(dob) : null;
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 3) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    }
  };

  // Validating Date of Birth
  const isValidDate = (dateOfBirth: string): boolean => {
    const dob = new Date(dateOfBirth);
    const today = new Date();

    if (dob > today) {
      return false;
    }
    return true;
  };

  // Validation form
  const validateForm = (): string | true => {
    if (patientData.firstName.length === 0) {
      return "First Name is required";
    }
    if (patientData.lastName.length === 0) {
      return "Last Name is required";
    }
    if (patientData.dateOfBirth.trim().length === 0) {
      return "Patient Date of Birth is required";
    }
    if (!isValidDate(patientData.dateOfBirth)) {
      return "Invalid Date of Birth. Please enter a valid Date of Birth";
    }
    if (patientData.gender.trim().length === 0) {
      return "Patient Gender is required";
    }
    if (patientData.address.trim().length === 0) {
      return "Address is required for registration";
    }
    if (patientData.state.trim().length === 0) {
      return "State is required for registration";
    }
    if (patientData.city.trim().length === 0) {
      return "City is required for registration";
    }
    if (patientData.pincode.trim().length === 0) {
      return "Pincode is required for registration";
    }
    if (patientData.pincode.length < 6) {
      return "Invalid Pincode";
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Combine firstName and lastName into full name
    const fullName = `${patientData.firstName.trim()} ${patientData.lastName.trim()}`;
    setPatientData((prev) => ({
      ...prev,
      name: fullName,
    }));

    console.log("Submitting...", {
      ...patientData,
      name: fullName,
    });

    // OTP Verification (if not verified yet)
    if (!isVerified) {
      const otpValue = otp.join("");
      if (otpValue.length !== 4) {
        toast.error("Please enter complete OTP");
        return;
      }

      try {
        setIsVerifying(true);
        const response = await PatientService.verifyOTP(mobileNumber, otpValue);

        if (response.success) {
          toast.success(response.message);
          setIsVerified(true);
        } else {
          toast.error(response.message || "OTP verification failed.");
        }
      } catch (error) {
        console.log(error);
        toast.error("Something went wrong during OTP verification.");
      } finally {
        setIsVerifying(false);
      }
      return; // Stop further execution until OTP is verified
    }

    const validationResult = validateForm();
    if (validationResult != true) {
      toast.error(validationResult);
      return;
    }

    try {
      // Combine firstName and lastName into name for submission
      const combinedFullName = `${patientData.firstName.trim()} ${patientData.lastName.trim()}`;

      const user = await AuthService.fetchUserDetails();
      console.log("\n\nuserr", user);

      if (initialData) {
        // Editing an existing patient
        const response = await PatientService.updatePatient(initialData.id, {
          ...patientData,
          name: combinedFullName,
        });

        if (response.success) {
          toast.success("Patient updated successfully!");
          onSuccess({
            ...patientData,
            name: combinedFullName,
          });
          onClose();
        } else {
          toast.error(response.message || "Failed to update patient.");
        }
      } else {
        // Adding a new patient
        const formData = new FormData();
        if (user?.role?.toLowerCase() === "clinic") {
          formData.append("clinicId", user?.id);
        } else if (user?.role?.toLowerCase() === "medicalcamp") {
          formData.append("medicalCampId", user?.id);
        }
        formData.append("mobileNumber", mobileNumber);
        formData.append("role", patientData.role);
        formData.append("name", combinedFullName);
        formData.append("age", formatAgeString(patientData.age));
        formData.append("gender", patientData.gender);
        if (patientData.profileImage instanceof File) {
          formData.append("profileImage", patientData.profileImage);
        }
        formData.append("aadharNumber", patientData.aadharNumber);
        formData.append("dateOfBirth", patientData.dateOfBirth);
        formData.append("address", patientData.address);
        formData.append("pincode", patientData.pincode);
        formData.append("city", patientData.city);
        formData.append("state", patientData.state);

        const response = await PatientService.addNewPatient(formData);

        if (response.success) {
          toast.success(response.message);
          onSuccess({
            ...patientData,
            id: response.data.id,
            name: combinedFullName,
          });
        } else {
          toast.error(response.message || "Failed to add patient.");
        }
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong");
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (file) {
      console.log("Selected File:", file); // Debugging log

      setPatientData((prev) => {
        const updatedData = { ...prev, profileImage: file };
        console.log("Updated PatientData:", updatedData); // Debugging log
        return updatedData;
      });
    }
  };

  function startCountdown(durationInSeconds: number) {
    let remainingTime = durationInSeconds;
    const countdownInterval = setInterval(() => {
      if (remainingTime >= 0) {
        const minutes = Math.floor(remainingTime / 60);
        const seconds = remainingTime % 60;

        setTimeLeft(
          `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
            2,
            "0",
          )}`,
        );

        remainingTime--;
      } else {
        clearInterval(countdownInterval);
        setTimerOn(false);
      }
    }, 1000);
  }

  const handleSendOTP = async () => {
    try {
      setIsVerifying(true);
      setTimerOn(true);
      const response = await PatientService.sendOTP(mobileNumber);
      if (response.success) {
        startCountdown(60);
        toast.success(response.message);
      }
    } catch (error) {
      console.log(error);
      toast.error("Something went wrong");
    }
  };

  useEffect(() => {
    if (mobileNumber.length === 10 && !isVerifying && !isVerified) {
      handleSendOTP();
    }
  }, [mobileNumber, isVerifying, isVerified]);

  const handleDeletePhoto = async (imgType: string) => {
    if (!patientData?.userId) return;

    try {
      const response = await PatientService.DeletePatientImage(
        patientData.userId,
      );

      if (response.success) {
        setPatientData((prev) => ({
          ...prev,
          [imgType]: "",
        }));
      } else {
        console.error("Failed to delete image:", response.message);
        alert("Failed to delete image. Please try again.");
      }
    } catch (error) {
      console.error("Error deleting image:", error);
      alert("Something went wrong.");
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
    setIsDragging(true);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(e.dataTransfer.files).filter((file) =>
        file.type.startsWith("image/"),
      );
      if (droppedFiles.length > 0) {
        setPatientData({ ...patientData, profileImage: droppedFiles[0] });
      } else {
        toast.error("Please drop only image files");
      }
      // Clear the dataTransfer object
      e.dataTransfer.clearData();
    }
  };

  return (
    <div className="absolute z-20 inset-0" style={{ height: "129vh" }}>
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url(${transparentBg})`,
          height: "inherit",
        }}
      />
      <div className=" relative w-full h-full flex items-start justify-center overflow-auto">
        <div className="bg-white rounded-xl w-[650px] shadow-xl mt-10">
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="text-lg font-medium text-emerald-600">
              {initialData ? "UPDATE PATIENT" : "ADD NEW PATIENT"}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {!isVerified ? (
              <>
                <div className="space-y-2">
                  <label className="block text-sm text-gray-600 font-medium">
                    Mobile Number
                  </label>
                  <div className="px-2 flex items-center gap-2 border border-gray-200 rounded-lg bg-gray-50">
                    <input
                      type="tel"
                      value={mobileNumber}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d{0,10}$/.test(value)) {
                          setMobileNumber(value);
                        }
                      }}
                      disabled={
                        isVerifying || (initialMobileNumber && !isVerified)
                      }
                      className="pr-2 border-none outline-none bg-transparent flex-1 py-2 selection:disabled:opacity-70"
                      placeholder="Enter mobile number"
                    />

                    {mobileNumber.length === 10 &&
                      !isVerifying &&
                      !isVerified && (
                        <span
                          onClick={handleSendOTP}
                          className="text-sm text-emerald-600 font-medium whitespace-nowrap"
                        >
                          Sending OTP...
                        </span>
                      )}
                    {isVerifying && (
                      <button
                        onClick={handleSendOTP}
                        className={`text-sm font-medium resendOtpButton ${
                          timerOn
                            ? "pointer-events-none text-pink-600/60"
                            : "text-pink-600"
                        }`}
                      >
                        Resend OTP
                      </button>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex justify-between items-center gap-3">
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        id={`otp-${index}`}
                        type="text"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        className="w-full h-12 text-center text-lg font-medium border border-gray-300 rounded-lg focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                      />
                    ))}
                  </div>
                  <div className="text-center text-sm text-gray-500">
                    {timeLeft} Secs
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="space-y-4">
                  {patientData.profileImage && (
                    <div className="flex flex-col items-center justify-center">
                      {/* Profile Image Display & Upload */}
                      <label
                        ref={dropZoneRef}
                        htmlFor="patient-image"
                        className={`relative w-24 h-24 mb-2 rounded-full border border-gray-300 flex items-center justify-center bg-gray-50 cursor-pointer hover:bg-gray-100 overflow-hidden ${
                          isDragging
                            ? "border-2 border-dashed border-emerald-500 bg-emerald-50"
                            : ""
                        }`}
                        onDragEnter={handleDragEnter}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                      >
                        {/* Hidden File Input */}
                        <input
                          type="file"
                          id="patient-image"
                          accept="image/*"
                          className="hidden"
                          onChange={handleImageUpload}
                        />

                        {/* Profile Image Preview */}
                        {patientData.profileImage ? (
                          <img
                            src={
                              patientData.profileImage instanceof File
                                ? URL.createObjectURL(patientData.profileImage)
                                : patientData.profileImage
                            }
                            alt="Patient"
                            className="w-full h-full rounded-full object-cover"
                          />
                        ) : (
                          <span className="text-gray-400 text-sm">
                            Upload Image
                          </span>
                        )}
                      </label>

                      {/* Edit & Delete Buttons */}
                      <div className="flex gap-3">
                        {/* Edit Button */}
                        <label
                          htmlFor="patient-image"
                          className="cursor-pointer"
                        >
                          <SquarePen className="w-5 h-5 text-gray-600" />
                        </label>

                        {/* Delete Button */}
                        <button
                          onClick={() => handleDeletePhoto("profileImage")}
                          type="button"
                          className="text-sm text-[#FF6C6C]"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  )}

                  {!patientData?.profileImage && (
                    <label
                      htmlFor="patient-image"
                      className={`cursor-pointer  rounded-md flex items-center flex-col justify-center bg-gray-100 py-2 ${
                        isDragging
                          ? "border-2 border-dashed border-emerald-500 bg-emerald-50"
                          : ""
                      } `}
                      style={{ width: "calc(100%)" }}
                      onDragEnter={handleDragEnter}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    >
                      <input
                        type="file"
                        id="patient-image"
                        accept="image/*"
                        className="hidden"
                        onChange={handleImageUpload}
                      />
                      <label
                        htmlFor="patient-image"
                        className="w-11 h-11 mx-auto mb-2 rounded-full bg-emerald-600 flex items-center justify-center hover:bg-emerald-600/70"
                      >
                        <ImageUp className="w-5 h-5 text-white" />
                      </label>
                      <p className="text-xs text-gray-500">
                        Click to upload or drag and drop your photo
                      </p>
                      <p className="text-xs text-gray-500">
                        Upload photo (optional)
                      </p>
                    </label>
                  )}

                  {/* First Name and Last Name fields (replacing Full Name) */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="block text-sm text-gray-600 font-medium">
                        First Name
                      </label>
                      <input
                        type="text"
                        value={patientData.firstName}
                        onChange={(e) => {
                          let value = e.target.value.trimStart();
                          // Allow letters and spaces, replace multiple spaces with single space
                          value = value.replace(/\s{2,}/g, " ");
                          if (/^[A-Za-z\s]*$/.test(value)) {
                            setPatientData((prev) => ({
                              ...prev,
                              firstName: value,
                            }));
                          }
                        }}
                        onBlur={() => {
                          setPatientData((prev) => ({
                            ...prev,
                            firstName: prev.firstName.trim(),
                          }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-emerald-500"
                        placeholder="Enter first name"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm text-gray-600 font-medium">
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={patientData.lastName}
                        onChange={(e) => {
                          let value = e.target.value.trimStart();
                          // Allow letters and spaces, replace multiple spaces with single space
                          value = value.replace(/\s{2,}/g, " ");
                          if (/^[A-Za-z\s]*$/.test(value)) {
                            setPatientData((prev) => ({
                              ...prev,
                              lastName: value,
                            }));
                          }
                        }}
                        onBlur={() => {
                          setPatientData((prev) => ({
                            ...prev,
                            lastName: prev.lastName.trim(),
                          }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-emerald-500"
                        placeholder="Enter last name"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2 relative">
                      <label className="block text-sm text-gray-600 font-medium">
                        Date of Birth
                      </label>
                      <div className="">
                        {/* Mui Date Input */}
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <div className="w-full">
                            <DatePicker
                              value={formatDateOfBirth(patientData.dateOfBirth)}
                              onChange={handleDOBChange}
                              disableFuture
                              views={["year", "month", "day"]}
                              slots={{
                                textField: TextField,
                              }}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  size: "small",
                                  className: "bg-white rounded-md",
                                  InputLabelProps: {
                                    className: "text-gray-700",
                                  },
                                },
                              }}
                            />
                          </div>
                        </LocalizationProvider>
                        {/* Mui Date Input */}
                      </div>
                    </div>

                    <div className="space-y-2 relative">
                      <label className="block text-sm text-gray-600 font-medium">
                        Age (Years)
                      </label>
                      <input
                        type="text"
                        value={(() => {
                          if (!patientData.age) return "";
                          const match = String(patientData.age).match(
                            /(\d+)\s*yrs?/,
                          );
                          if (match) return match[1];
                          if (/^\d+$/.test(String(patientData.age)))
                            return String(patientData.age);
                          return "";
                        })()}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, "");

                          let calculatedDob = "";
                          if (value) {
                            const years = parseInt(value, 10);
                            const today = new Date();
                            today.setFullYear(today.getFullYear() - years);
                            calculatedDob = today.toISOString().split("T")[0];
                          }

                          setPatientData((prev) => ({
                            ...prev,
                            age: value ? `${value} yrs` : "",
                            dateOfBirth: calculatedDob,
                          }));
                        }}
                        maxLength={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-emerald-500 h-[40px]"
                        placeholder="e.g. 25"
                      />
                    </div>

                    <div className="space-y-2 relative">
                      <label className="block text-sm text-gray-600 font-medium">
                        Gender
                      </label>
                      <select
                        value={patientData.gender}
                        onChange={(e) =>
                          setPatientData((prev) => ({
                            ...prev,
                            gender: e.target.value as NewPatientData["gender"],
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-emerald-500 h-[40px]"
                      >
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm text-gray-600 font-medium">
                      Aadhaar Number
                    </label>
                    <input
                      type="text"
                      value={patientData.aadharNumber}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, ""); // Allow only digits
                        if (value.length <= 12) {
                          setPatientData((prev) => ({
                            ...prev,
                            aadharNumber: value,
                          }));
                        }
                      }}
                      maxLength={12} // Prevents input beyond 12 characters
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-emerald-500"
                      placeholder="Enter patient Aadhaar number"
                    />
                  </div>
                  <div className="space-y-2">
                    <label
                      htmlFor="pincode"
                      className="block text-sm text-gray-600 font-medium"
                    >
                      Address
                    </label>
                    <input
                      type="text"
                      value={patientData.address}
                      onChange={(e) =>
                        setPatientData((prev) => ({
                          ...prev,
                          address: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-emerald-500"
                      placeholder="Enter patient address"
                    />
                  </div>

                  {/* City,Pincode and State */}
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="space-y-2">
                      <label
                        htmlFor="State"
                        className="block text-sm text-gray-600 font-medium"
                      >
                        State
                      </label>
                      <input
                        id="state"
                        name="state"
                        type="text"
                        value={patientData.state}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^[A-Za-z\s]*$/.test(value)) {
                            setPatientData((prev) => ({
                              ...prev,
                              state: value,
                            }));
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 outline-none rounded-lg focus:ring-1 focus:ring-emerald-500"
                        placeholder="Enter state"
                      />
                    </div>
                    <div className="space-y-2">
                      <label
                        htmlFor="city"
                        className="block text-sm text-gray-600 font-medium"
                      >
                        City
                      </label>
                      <input
                        id="city"
                        name="city"
                        type="text"
                        value={patientData.city}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^[A-Za-z\s]*$/.test(value)) {
                            setPatientData((prev) => ({
                              ...prev,
                              city: value,
                            }));
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 outline-none rounded-lg focus:ring-1 focus:ring-emerald-500"
                        placeholder="Enter city"
                      />
                    </div>
                    <div className="space-y-2">
                      <label
                        htmlFor="pincode"
                        className="block text-sm text-gray-600 font-medium"
                      >
                        Pincode
                      </label>
                      <input
                        type="text"
                        value={patientData.pincode}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value.length <= 6 && /^\d*$/.test(value)) {
                            setPatientData((prev) => ({
                              ...prev,
                              pincode: value,
                            }));
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 outline-none rounded-lg focus:ring-1 focus:ring-emerald-500"
                        placeholder="Enter patient pincode"
                      />
                    </div>
                  </div>
                  {/* City,pincode and state */}
                </div>
              </>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!isVerified && otp.join("").length !== 4}
                className="flex-1 px-4 py-2 text-sm font-bold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:bg-gray-200 disabled:cursor-not-allowed"
              >
                {initialData
                  ? "Update Patient"
                  : isVerified
                    ? "Add Patient"
                    : "Verify"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
